import ModalSurface from '../shared/ModalSurface';
import { useState, useEffect, useMemo, useCallback } from 'react';

function TransactionModal({ transaction, chartOfAccounts, onClose, onSave }) {
    const [formData, setFormData] = useState({
        date: transaction?.date || new Date().toISOString().split('T')[0],
        description: transaction?.description || '',
        merchant: transaction?.merchant || '',
        category: transaction?.category || '',
        type: transaction?.type || 'expense',
        amount: transaction?.amount || 0,
        memo: transaction?.memo || '',
        tags: transaction?.tags || '',
        fromTo: transaction?.from_to || ''  // Read from database as from_to
    });
     const [showFullDetails, setShowFullDetails] = useState(!!transaction);
     const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };
     return (
        <ModalSurface panelProps={{ 'data-m7-touch': true }} onClose={onClose} label={transaction ? 'Edit Transaction' : 'Add Transaction'}
            overlayClassName="fixed inset-0 bg-black bg-opacity-30 z-50" onOverlayClick={onClose}
            panelClassName="absolute top-0 left-0 right-0 bg-white shadow-2xl rounded-b-2xl mx-auto max-w-6xl animate-slideDown"
            panelStyle={{ animation: 'slideDown 0.3s ease-out', maxHeight: '90vh', overflowY: 'auto' }}>
                <style>{`
                    @keyframes slideDown {
                        from {
                            transform: translateY(-100%);
                            opacity: 0;
                        }
                        to {
                            transform: translateY(0);
                            opacity: 1;
                        }
                    }
                `}</style>
                
                <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-800">
                                {transaction ? 'Edit Transaction' : 'Add Transaction'}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">Complete transaction details below</p>
                        </div>
                        <button 
                            onClick={onClose} 
                            className="ia-modal-desktop-close text-gray-400 hover:text-gray-600 text-3xl leading-none"
                            title="Close"
                        >
                            ×
                        </button>
                    </div>
                </div>
                
                <div className="ia-modal-layout p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="ia-modal-body space-y-6">
                        {/* Basic Info Row */}
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                                <input 
                                    type="date" 
                                    value={formData.date} 
                                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]" 
                                    required 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                                <input 
                                    type="text" 
                                    value={formData.description} 
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]" 
                                    placeholder="e.g., Zelle Val Lynette"
                                    required 
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                                    <input 
                                        type="number" 
                                        step="0.01" 
                                        value={formData.amount === 0 ? '' : formData.amount}
                                        onChange={(e) => {
                                            const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                            setFormData({...formData, amount: isNaN(value) ? 0 : value});
                                        }}
                                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]" 
                                        placeholder="0.00" 
                                        required 
                                    />
                                </div>
                            </div>
                        </div>
                         {/* Expandable Details Section */}
                        <div className="border-t pt-4">
                            <button
                                type="button"
                                onClick={() => setShowFullDetails(!showFullDetails)}
                                className="flex items-center gap-2 text-[var(--accent-ink)] hover:text-[var(--accent-ink-hover)] font-medium mb-4 transition"
                            >
                                <span className="text-lg">{showFullDetails ? '▼' : '▶'}</span>
                                <span>Full Bank Description & Details</span>
                            </button>
                             {showFullDetails && (
                                <div className="grid grid-cols-2 gap-6 bg-gray-50 p-5 rounded-lg border border-gray-200">
                                    {/* Left Column */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Bank Description</label>
                                            <textarea
                                                value={formData.merchant}
                                                onChange={(e) => setFormData({...formData, merchant: e.target.value})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]"
                                                rows="3"
                                                placeholder="Zelle payment to Val Lynette JPM99atyog81"
                                            />
                                        </div>
                                         <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type *</label>
                                            <select 
                                                value={formData.type} 
                                                onChange={(e) => setFormData({...formData, type: e.target.value, category: ''})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]" 
                                                required
                                            >
                                                <option value="expense">Expense</option>
                                                <option value="income">Income</option>
                                            </select>
                                        </div>
                                         <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Account *</label>
                                            <select 
                                                value={formData.category} 
                                                onChange={(e) => setFormData({...formData, category: e.target.value})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]" 
                                                required
                                            >
                                                <option value="">Select account...</option>
                                                {chartOfAccounts.filter(c => c.type === formData.type).map(cat => (
                                                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                         <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">From/To (Vendor/Customer)</label>
                                            <select
                                                value={formData.fromTo}
                                                onChange={(e) => setFormData({...formData, fromTo: e.target.value})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]"
                                            >
                                                <option value="">Select vendor...</option>
                                                <option value="Val Lynette">Val Lynette</option>
                                                <option value="Shell Gas Station">Shell Gas Station</option>
                                                <option value="ABC Insurance">ABC Insurance</option>
                                            </select>
                                        </div>
                                    </div>
                                     {/* Right Column */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Memo</label>
                                            <textarea
                                                value={formData.memo}
                                                onChange={(e) => setFormData({...formData, memo: e.target.value})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]"
                                                rows="3"
                                                placeholder="Add notes about this transaction..."
                                            />
                                        </div>
                                         <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                                            <input
                                                type="text"
                                                value={formData.tags}
                                                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent-ink)]"
                                                placeholder="Add tags (comma separated)"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
                                        </div>
                                         <div className="border border-gray-200 rounded-lg p-3 bg-white">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Files</label>
                                            <button 
                                                type="button"
                                                className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-[var(--accent-ink)] hover:text-[var(--accent-ink)] transition"
                                            >
                                                📎 Upload Receipt or Document
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        </div>
                         {/* Action Buttons */}
                        <div className="ia-modal-actions flex justify-between items-center pt-4 border-t">
                            <div className="flex gap-2">
                                <button 
                                    type="button"
                                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                                    onClick={onClose}
                                >
                                    Cancel
                                </button>
                            </div>
                            <div className="flex gap-2">
                                <button 
                                    type="button"
                                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm"
                                >
                                    Split
                                </button>
                                <button 
                                    type="button"
                                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm"
                                >
                                    Create rule
                                </button>
                                <button 
                                    type="button"
                                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm"
                                >
                                    Exclude
                                </button>
                                <button 
                                    type="submit" 
                                    className="px-8 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm"
                                >
                                    Post
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
        </ModalSurface>
    );
}


export default TransactionModal;
